import axios from "axios";
import { getVisitorFaceData } from "../.././../utils/getVisitorFaceData";

import BASE_URL from "./../../../constants/baseURL";
import { syncVisitorData, setVisitorDetail } from "../memberSlice";
import { setSessionExpiry, markSessionExpired } from "../../../utils/session";

// Maps the raw get-visitor-detail response into the visitorData shape stored in
// redux. Shared by the initial sign-in and the background revalidation so both
// produce an identical object.
const buildVisitorData = (visitorData, dependentList) => ({
  ID: visitorData?._id,
  firstName: visitorData?.first_name,
  lastName: visitorData?.last_name,
  gender: visitorData?.gender,
  emailID: visitorData?.email_id || visitorData?.email,
  faceData: visitorData?.face_data,
  onWhatsapp: visitorData?.on_whatsapp,
  phoneNumber: visitorData?.phone_number,
  dependentList: dependentList,
  isDocVerified: visitorData?.is_doc_verified,
  userTypeIds: visitorData?.user_type_ids,
  isPrimary: visitorData?.is_primary,
  premisesID: visitorData?.premises_id,
  premisesName: visitorData?.premises_name,
  employeeDetails: visitorData?.employee_details,
  guardId: visitorData?.is_guard,
});

export default async function signIn(
  { authToken, agentName, eventID, ID, expiryTimestamp, newAuthToken = false },
  thunkAPI
) {
  const queryParams = new URLSearchParams(window.location.search);
  const isGuardLogin = queryParams.get("guard_login") == "true";

  if (isGuardLogin) {
    sessionStorage.setItem("auth_token_with_guard", authToken);
    localStorage.setItem("auth_token_with_guard", authToken);
  } else {
    sessionStorage.setItem("authToken", authToken);
    localStorage.setItem("authToken", authToken);
  }

  // Track when this session lapses: prefer the expiry_timestamp returned by
  // verify-otp, fall back to the token's own exp claim (see utils/session).
  setSessionExpiry(expiryTimestamp, authToken);

  sessionStorage.setItem("is_guard_created", ID);

  const location = window.location.pathname;

  const checkInRoute = [
    // "/sign-up",
    // "/sign-in",
    // "/manual-entry",
    // "/resend-notification",
    // "/manual-verification",
    // "/main-gate-entry-back",
    // "/main-gate-entry-front",
    // "/add-booking",
    // "/error-500",
  ].filter((item) => location == item);

  const axiosInstance = axios.create({
    baseURL: BASE_URL.mox_api.url + BASE_URL.mox_api.version,
    headers: {
      Authorization:
        "Bearer " + authToken || sessionStorage.getItem("authToken"),
      "Content-Type": "application/json",
    },
  });

  let visitorData;
  let dependentList;

  if (
    window.location.pathname == "/face-verification" ||
    window.location.pathname == "/liveness"
  ) {
    // await axiosInstance({
    //   url: `event/visitor/visitor-journey-started`,
    //   method: "POST",
    // });
  }

  if (!Boolean(checkInRoute.length) || newAuthToken) {
    const requestedId = ID || sessionStorage.getItem("visitorId");

    const fetchVisitorDetail = () =>
      axiosInstance({
        url: `admin/profile/get-visitor-detail${
          !isGuardLogin && requestedId ? "?visitor_id=" + requestedId : ""
        }`,
        method: "POST",
        data: {
          encrypted_face_data: getVisitorFaceData(requestedId),
        },
      }).then((res) => res.data.data);

    // Reuse the cached visitor detail across page refreshes (within the same
    // browser session) so we don't block on get-visitor-detail every reload.
    // A fresh token (newAuthToken) always refetches; user mutations clear this
    // cache (see memberSlice).
    let cachedVisitor = null;
    try {
      const raw = sessionStorage.getItem("visitorDetailCache");
      cachedVisitor = raw ? JSON.parse(raw) : null;
    } catch (e) {
      cachedVisitor = null;
    }

    const canUseCache =
      !newAuthToken &&
      cachedVisitor &&
      (!requestedId || cachedVisitor._id === requestedId);

    if (canUseCache) {
      // Stale-while-revalidate: render the cached data instantly, then refresh
      // from the server in the background and patch redux only if the value has
      // actually changed (e.g. a status flipped server-side).
      visitorData = cachedVisitor;

      fetchVisitorDetail()
        .then((fresh) => {
          if (
            fresh &&
            JSON.stringify(fresh) !== JSON.stringify(cachedVisitor)
          ) {
            sessionStorage.setItem("visitorDetailCache", JSON.stringify(fresh));
            thunkAPI?.dispatch?.(
              syncVisitorData(buildVisitorData(fresh, fresh?.dependents || []))
            );
            thunkAPI?.dispatch?.(setVisitorDetail(fresh));
          }
        })
        .catch(() => {
          // Ignore background errors; the cached data is already shown.
        });
    } else {
      try {
        visitorData = await fetchVisitorDetail();

        if (visitorData) {
          sessionStorage.setItem(
            "visitorDetailCache",
            JSON.stringify(visitorData)
          );
        }
      } catch (error) {
        if (error.status == 401) {
          let autoRefresh = error.response.data.detail.auto_refresh;
          let refreshToken = error.response.data.detail.refresh_token;
          if (!autoRefresh) {
            localStorage.removeItem("authToken");
            sessionStorage.removeItem("authToken");
            localStorage.removeItem("auth_token_with_guard");
            sessionStorage.removeItem("auth_token_with_guard");
            markSessionExpired();
            window.location.href = "/login";
          } else {
            localStorage.removeItem("authToken");
            sessionStorage.removeItem("authToken");
            localStorage.removeItem("auth_token_with_guard");
            sessionStorage.removeItem("auth_token_with_guard");
            sessionStorage.setItem("authToken", refreshToken);
            localStorage.setItem("authToken", refreshToken);
            if (isGuardLogin) {
              sessionStorage.setItem("auth_token_with_guard", refreshToken);
              localStorage.setItem("auth_token_with_guard", refreshToken);
            }
            setSessionExpiry(null, refreshToken);
            window.location.reload();
          }
        }
      }
    }

    // Dependents now come embedded in the get-visitor-detail response
    dependentList = visitorData?.dependents || [];

    localStorage.setItem("visitorDataID", visitorData?._id);
  }

  return {
    authToken: authToken,
    displayName: agentName,
    eventID: eventID,
    visitorData: {
      ID: visitorData?._id,
      firstName: visitorData?.first_name,
      lastName: visitorData?.last_name,
      gender: visitorData?.gender,
      emailID: visitorData?.email_id || visitorData?.email,
      faceData: visitorData?.face_data,
      onWhatsapp: visitorData?.on_whatsapp,
      phoneNumber: visitorData?.phone_number,
      dependentList: dependentList,
      isDocVerified: visitorData?.is_doc_verified,
      userTypeIds: visitorData?.user_type_ids,
      isPrimary: visitorData?.is_primary,
      premisesID:
        visitorData?.premises_id ||
        visitorData?.employee_details?.[0]?.premises_id,
      premisesName: visitorData?.premises_name,
      employeeDetails: visitorData?.employee_details,
      guardId: visitorData?.is_guard,
    },
    businessData: {
      name: "",
      logo: "https://dolphinchat-chat.s3.ap-south-1.amazonaws.com/mpsedc/digipass/events1761823450063_1761823451822_images.png",
      color: "",
      image: "",
      fontFamily: "",
    },
  };
}
